import { useState, useEffect } from 'react';
import {useAuth} from '../../Auth/AuthContext'; 
import styles from './Profile.module.css';
import { User, Shield, Users, GraduationCap, Settings, BookOpen, AlertCircle, Camera } from 'lucide-react';
import EditarPerfil from './EditarPerfil';
import CambiarContrasena from './CambiarContrasenia'; 
import AsociarEstudiantes from './AsociarEstudiante'; 
import ProfileImageUploader from './ProfileImagesUploader';

const PerfilUsuario = () => {
  const {userData, loading: authLoading, getToken} = useAuth(); 
  const token = getToken();
  const [error, setError] = useState(null);
  const [modalEditar, setModalEditar] = useState(false);
  const [modalCambiarContrasena, setModalCambiarContrasena] = useState(false);
  const [modalAsociarEstudiantes, setModalAsociarEstudiantes] = useState(false); 
  const [modalImagenPerfil, setModalImagenPerfil] = useState(false);
  const [editData, setEditData] = useState({});
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [progresoData, setProgresoData] = useState(null);
  const [loadingProgreso, setLoadingProgreso] = useState(false);
  const [currentProfileImage, setCurrentProfileImage] = useState(null);

  useEffect(() => {
    if (userData) { 
      setEditData(userData); 
      
      // Cargar imagen de perfil si existe
      if (userData.profile_image) {
        setCurrentProfileImage(userData.profile_image);
      }
      
      // Cargar progreso solo para estudiantes
      if (userData.tipo === 1) {
        fetchProgresoPasos();
      }
    }
  }, [userData]); 

  const fetchProgresoPasos = async () => {
    if (!userData?.id || !token) return;
    
    setLoadingProgreso(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/perfil/progreso-general/${userData.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProgresoData(data);
      } else {
        console.error('Error al cargar progreso de pasos');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoadingProgreso(false);
    }
  };

  const getInitial = () => { 
    if (userData && userData.nombre) { 
      return userData.nombre.charAt(0).toUpperCase(); 
    }
    return 'U'; 
  }; 

  const handleEditProfile = () => {
    setModalEditar(true);
    setShowSettingsMenu(false);
  };

  const handleChangePassword = () => {
    setModalCambiarContrasena(true); 
    setShowSettingsMenu(false);
  };

  const handleAssociateStudents = () => {
    setModalAsociarEstudiantes(true);
    setShowSettingsMenu(false);
  };

  const handleChangeProfileImage = () => {
    setModalImagenPerfil(true);
    setShowSettingsMenu(false);
  };

  const handleImageUpdate = (newImageBase64) => {
    setCurrentProfileImage(newImageBase64);
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'No especificado';
    return new Date(fecha).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTipoUsuario = (tipo) => {
    switch (tipo) {
      case 1: return 'Estudiante';
      case 2: return 'Administrador';
      case 3: return 'Tutor';
      default: return 'Usuario ???';
    }
  };
  
  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 1: return <GraduationCap className={styles.iconSize} />;
      case 2: return <Shield className={styles.iconSize} />;
      case 3: return <Users className={styles.iconSize} />;
      default: return <User className={styles.iconSize} />;
    }
  };

  const getUserImage = () => {
    // Primero verifica si hay una imagen base64 actualizada
    if (currentProfileImage) {
      return currentProfileImage;
    }
    
    // Luego verifica la imagen original del usuario
    if (userData && userData.profile_image) {
      return userData.profile_image;
    }
    
    // Si había una imagen con el sistema anterior (archivo)
    if (userData && userData.image) {
      return `${process.env.REACT_APP_API_URL}/perfil/profile-images/${userData.image}`;
    }
    
    return null; 
  };

  const getProgressColor = (progreso) => {
    if (progreso === 0) return '#e5e7eb'; // gris
    if (progreso < 30) return '#ef4444'; // rojo
    if (progreso < 70) return '#f59e0b'; // amarillo
    return '#10b981'; // verde
  };

  const renderProgresoCard = () => {
    if (userData.tipo !== 1) return null;

    if (loadingProgreso) {
      return (
        <div className={styles.progressCard}>
          <h3>
            <BookOpen className={styles.iconSize} />
            Progreso
          </h3>
          <div className={styles.loadingProgreso}>
            <div className={styles.loadingSpinner}></div>
            <p>Cargando progreso...</p>
          </div>
        </div>
      );
    }

    if (!progresoData) {
      return (
        <div className={styles.progressCard}>
          <h3>
            <BookOpen className={styles.iconSize} />
            Progreso
          </h3>
          <div className={styles.errorProgreso}>
            <AlertCircle className={styles.iconSize} />
            <p>No se pudo cargar el progreso</p>
          </div>
        </div>
      );
    }

    const { pasos } = progresoData;

    return (
      <div className={styles.progressCard}>
        <h3>
          Progreso
        </h3>
        
        {/* Lista de pasos */}
        <div className={styles.pasosList}>
          {pasos.map((paso) => (
            <div key={paso.pa_clave} className={styles.pasoItem}>
              <div className={styles.pasoInfo}>
                <span className={styles.pasoTitulo}>{paso.pa_titulo}</span>
              </div>
              <div className={styles.pasoProgress}>
                <div className={styles.progressBarSmall}>
                  <div 
                    className={styles.progressFillSmall}
                    style={{ 
                      width: `${paso.progreso_paso}%`,
                      backgroundColor: getProgressColor(paso.progreso_paso)
                    }}
                  />
                </div>
                <span className={styles.porcentajeSmall}>{paso.progreso_paso}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (authLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <div className={styles.loadingSpinner}></div>
          <p className={styles.loadingText}>Cargando perfil...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContainer}> 
          <p className={styles.loadingText}> Error al cargar datos :( </p>
        </div>
      </div>
    )
  }

  if (!userData) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContainer}> 
          <p className={styles.loadingText}> Error al cargar perfil :( </p>
        </div>
      </div>
    )
  }

  const sections = [
    {
      title: 'Información Personal',
      fields: [
        userData.tipo !== 2 && userData.curp && { label: 'CURP', value: userData.curp },
        userData.tipo == 1 && userData.fecha_nacimiento && { label: 'Fecha de nacimiento', value: formatearFecha(userData.fecha_nacimiento) },
        { label: 'Fecha de registro', value: formatearFecha(userData.fecha_registro) },
        userData.tipo === 1 && userData.estado && { label: 'Estado', value: userData.estado },
        userData.tipo === 1 && userData.municipio && { label: 'Municipio', value: userData.municipio }
      ].filter(Boolean)
    },
    {
      title: 'Contacto',
      fields: [
        { label: 'Correo electrónico', value: userData.correo },
        userData.tipo !== 2 && userData.telefono && { label: 'Teléfono', value: '+' + userData.telefono },
        userData.tipo === 1 && userData.correo_tutor && { label: 'Correo del tutor', value: userData.correo_tutor },
      ].filter(Boolean)
    },
    ...(userData.tipo === 1 ? [{
      title: 'Información Académica',
      fields: [
        userData.bachillerato && { label: 'Bachillerato', value: userData.bachillerato },
        userData.institucion && { label: 'Institución', value: userData.institucion },
        userData.matricula && { label: 'Matrícula', value: userData.matricula },
      ].filter(Boolean)
    }] : []),
  ].filter(section => section.fields.length > 0);

  return (
    <div className={styles.mainContainer}>
      <div className={styles.cardsWrapper}>
        {/* Card principal con información del usuario */}
        <div className={styles.userCard}>
          {/* Menú de configuración */}
          <div className={styles.settingsMenuContainer}>
            <button 
              onClick={() => setShowSettingsMenu(!showSettingsMenu)}
              className={styles.settingsButton}
            >
              <Settings className={styles.iconSize} />
            </button>
            
            {showSettingsMenu && (
              <div className={styles.dropdownMenu}>
                <button
                  onClick={handleEditProfile}
                  className={styles.dropdownItem}
                >
                  Editar Perfil
                </button>
                <div className={styles.dropdownDivider}></div>
                <button
                  onClick={handleChangePassword}
                  className={styles.dropdownItem}
                >
                  Cambiar Contraseña
                </button>
                <div className={styles.dropdownDivider}></div>
                {userData?.tipo === 3 && (
                  <>
                    <div className={styles.dropdownDivider}></div>
                    <button
                      onClick={handleAssociateStudents}
                      className={styles.dropdownItem}
                    >
                      Asociar Estudiantes
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Header del perfil */}
          <div className={styles.profileHeader}>
            <div className={styles.profileImageContainer}>
              {getUserImage() ? (
                <img src={getUserImage()} alt="Foto de perfil" className={styles.profileImage} />
              ) : (
                <div className={styles.userAvatar}>
                  {getInitial()}
                </div>
              )}
              <button 
                onClick={handleChangeProfileImage}
                className={styles.changeImageButton}
                title="Cambiar imagen de perfil"
              >
                <Camera size={16} />
              </button>
            </div>
            <h1 className={styles.profileName}>
              {userData.nombre} {userData.apellido_paterno} {userData.apellido_materno}
            </h1>
            <div className={styles.profileType}>
              {getTipoIcon(userData.tipo)}
              {getTipoUsuario(userData.tipo)}
            </div>
          </div>

          {/* Secciones de información */}
          {sections.map((section, index) => (
            <div key={index} className={styles.infoSection}>
              <h3>{section.title}</h3>
              <div className={styles.infoGrid}>
                {section.fields.map((field, fieldIndex) => (
                  <div key={fieldIndex} className={styles.infoItem}>
                    <span className={styles.infoLabel}>{field.label}:</span>
                    <span className={styles.infoValue}>{field.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Card de progreso (solo para estudiantes) */}
        {renderProgresoCard()}
      </div>

      {/* Modales */}
      {modalEditar && (
        <EditarPerfil
          abrirModal={modalEditar}
          cerrarModal={() => setModalEditar(false)}
          userData={editData}
          onUpdate={setEditData}
        />
      )}

      {modalCambiarContrasena && (
        <CambiarContrasena
          abrirModal={modalCambiarContrasena}
          cerrarModal={() => setModalCambiarContrasena(false)}
        />
      )}

      {modalAsociarEstudiantes && (
        <AsociarEstudiantes
          abrirModal={modalAsociarEstudiantes}
          cerrarModal={() => setModalAsociarEstudiantes(false)}
        />
      )}

      {modalImagenPerfil && (
        <ProfileImageUploader
          isOpen={modalImagenPerfil}
          onClose={() => setModalImagenPerfil(false)}
          onImageUpdate={handleImageUpdate}
          currentImage={getUserImage()}
          token={token}
        />
      )}
    </div>
  );
};

export default PerfilUsuario;